/**
 * SOLID Review
 * S — Strong. One concern: the frontend's composition root — the one assembled graph, the Framework's host
 *     resources, and the one call made outside the provider.
 * O — Good. A new host resource is one field; a new component is the assembly's.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Three exports.
 * D — Good. The composition root: it may name concretes (`BrowserPlatform` names most of them), and it is
 *     the only module in framework/ besides that one which does.
 */
import type { IConfigHandlerConnectionBanner } from '../../infrastructure/graphql/ApolloClientFactory';
import { UiState } from '../stores/UiState';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { IConfigDepFrameworkHost } from '../uml-components/ComponentFramework';
import { ClientRequests } from '../client/ClientRequests';
import { ViewModelFactory, type IConfigDepViewModels } from '../viewmodels/ViewModelFactory';
import { BrowserViewPlatform } from './BrowserViewPlatform';
import { FrontendAssembly, type IEntityFrontendGraph } from './FrontendAssembly';
import { browserPlatform } from './BrowserPlatform';
import { timed } from './UserTiming';
import { ClientErrorReporting, type IConfigDepClientErrorReporting } from './ClientErrorReporting';

/**
 * THE ONE ASSEMBLED FRONTEND GRAPH (frontend strict-ports plan, FE1d; all four components since FE6a,
 * 2026-10-04): constructed, connected through `FrontendConnectors`, checked and built ONCE, here, at module
 * load — never in a render. App.tsx hands its Framework component to `ComponentFrameworkProvider`.
 *
 * This module was `adapterRegistry.ts` until FE6a: beside the graph it built the legacy adapters the
 * Framework's adapter sockets carried (Apollo room, game and session adapters, the room lookup, the
 * websocket monitor) into an `IAdapterRegistry` the Framework connected itself from. Those sockets, the
 * registry and the self-connection are gone; what is left is the graph, the two host resources the
 * Framework's context carries, and MaintenanceBanner's call.
 *
 * Kept as a module of its own (not App.tsx) for the reason it was split out on 2026-08-19: the port and UML
 * generators read the REAL assembled graph — a diagram drawn from a fixture documents the fixture — and
 * importing App.tsx pulls in the React tree.
 *
 * Timed as the User Timing measure `ml3d:assemble` (FE1's stop rule): the platform built, then construct, connect, check,
 * build. Since frontend-infrastructure-oo P2 (§28.287) the platform holds the websocket monitor and the Apollo client, which
 * built themselves on import before, outside the measure.
 */
const ASSEMBLE_MEASURE = 'ml3d:assemble';

/**
 * The Framework's own pieces, built once (frontend-framework-oo P1, §28.293): the browser as its view models reach it, the request
 * contexts (their counter was module state), the screens' shared presentation state (the UI store was a module singleton).
 */
const viewPlatform = new BrowserViewPlatform();
const requests = new ClientRequests(viewPlatform.clock);
const ui = new UiState();

/**
 * Apollo's slow-request banner, wired to the screens' presentation state — moved from App.tsx in strict-ports FE7, since naming
 * the Apollo module is composition work. Infrastructure must not reach into the Framework's state, so the setter is handed to the
 * platform, which builds the Apollo client with it (frontend-infrastructure-oo P2; it was a module setter called here before the
 * client existed).
 */
const configHandlerConnectionBanner: IConfigHandlerConnectionBanner = {
  onSlowStart: () => ui.setConnectionBanner('Server is busy — hang on…'),
  onSlowEnd: () => ui.setConnectionBanner(null),
};

/**
 * CLIENT ERROR REPORTING (plan 2026-10-09, EP7). Built before the graph — its port observer goes to every
 * connector — and bound to the assembled `ClientUseCases` socket after. Then the window's `error` and
 * `unhandledrejection` listeners, and the flag the boot guard in index.html stands down on (EP8): from here
 * on this object reports, and the inline guard would only double it.
 */
const clientErrorReportingDeps: IConfigDepClientErrorReporting = { now: () => Date.now(), context: () => requests.context() };
const clientErrors = new ClientErrorReporting(clientErrorReportingDeps);

const assembled = timed(performance, ASSEMBLE_MEASURE, () => {
  const platform = browserPlatform(clientErrors, configHandlerConnectionBanner);
  return { platform, graph: FrontendAssembly.assemble(platform) };
});
export const frontendGraph: IEntityFrontendGraph = assembled.graph;

clientErrors.bind(frontendGraph.framework.requiredPorts.clientUseCasesSocket.getInterface());
/* c8 ignore next 4 -- under Node (the port and UML generators) there is no event target and no boot guard to stand down. */
if (typeof window.addEventListener === 'function') {
  clientErrors.install(window, window.location.origin);
  (window as unknown as { __ml3dBooted?: boolean }).__ml3dBooted = true;
}

/** React's `onCaughtError` (main.tsx): a render an error boundary caught is reported, and still logged. */
export const reportRenderError = (error: unknown, info: { readonly componentStack?: string }): void => {
  console.error('[ErrorBoundary]', error);
  clientErrors.renderError(error, info);
};

/**
 * The Framework's host resources (rule 2.3): the LAZY LiveKit client factory — the SDK chunk is
 * downloaded only when a player opts into video — and the websocket's live state, for AppRoot's watchdog.
 */
export const frameworkHost: IConfigDepFrameworkHost = {
  liveKitClientFactory: assembled.platform.liveKit,
  wsConnectionMonitor: assembled.platform.monitor,
};

/**
 * MaintenanceBanner's one call, for its mount OUTSIDE <ComponentFrameworkProvider>: the banner that says
 * "the backend is unreachable" must not itself depend on the provider (the 2026-08-17 white screen). It
 * is the Application's `checkServerHealth`, through the assembled Framework's own socket — guarded by the
 * use cases' contract, like every call React makes.
 */
const CHECK_HEALTH: TClientUseCaseInput<'checkServerHealth'> = {};
export const checkServerHealth = (): ReturnType<IPortClientUseCases['checkServerHealth']> =>
  frontendGraph.framework.requiredPorts.clientUseCasesSocket.getInterface().checkServerHealth(requests.context(), CHECK_HEALTH);

/**
 * THE FRAMEWORK'S VIEW-MODEL FACTORY (frontend-framework-oo P1, §28.293), built once, over the assembled Framework component's two
 * ports — App.tsx hands it to the tree (`ViewModelProvider`).
 */
const viewModelDeps: IConfigDepViewModels = {
  useCases: frontendGraph.framework.requiredPorts.clientUseCasesSocket.getInterface(),
  view: frontendGraph.framework.requiredPorts.clientStateViewSocket.getInterface(),
  platform: viewPlatform,
  requests,
  ui,
  liveKit: assembled.platform.liveKit,
  connection: assembled.platform.monitor,
};
export const frameworkViews = new ViewModelFactory(viewModelDeps);
